// Primary English Curriculum - Years 2, 3, 4, 5
// Based on Vanuatu National Curriculum - Language & Communication
// 4 Strands: Listening & Speaking, Reading, Writing, Viewing

// ============================================
// YEAR 2 ENGLISH CURRICULUM
// Building on Year 1 Foundation
// ============================================
export const englishYear2 = {
  year: 2,
  subject: 'English',
  totalWeeks: 40,
  periodsPerWeek: 6,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Developing Reading Fluency & Simple Writing',
      scheme: [
        {
          week: 1,
          topic: 'Retelling Stories',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Listen attentively to short stories',
            'Retell stories in correct sequence',
            'Use expression when speaking'
          ],
          activities: [
            'Story listening sessions',
            'Sequencing picture cards',
            'Partner retelling practice'
          ],
          resources: ['Picture books', 'Sequence cards', 'Story props'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Welcome Back & Story Time',
              duration: '35 min',
              objectives: ['Listen to a complete story', 'Identify main characters'],
              introduction: 'Welcome children back. Read "The Very Hungry Caterpillar" or local story.',
              mainActivities: [
                { activity: 'Story Reading', duration: '15 min', description: 'Teacher reads story with expression' },
                { activity: 'Character Chat', duration: '10 min', description: 'Who is in the story? What happens?' },
                { activity: 'Story Response', duration: '10 min', description: 'Draw favorite part' }
              ],
              materials: ['Storybook', 'Drawing paper', 'Crayons'],
              assessment: 'Can identify main character',
              homework: 'Tell the story to someone at home'
            },
            {
              day: 'Tuesday',
              title: 'What Happened First?',
              duration: '35 min',
              objectives: ['Identify beginning of story', 'Use sequence words: first, then'],
              introduction: 'Review yesterday\'s story. What happened at the start?',
              mainActivities: [
                { activity: 'Beginning Hunt', duration: '10 min', description: 'Find the beginning in the story' },
                { activity: 'Sequence Words', duration: '15 min', description: 'Learn: first, then, next, finally' },
                { activity: 'Practice Sequencing', duration: '10 min', description: 'Put 3 pictures in order' }
              ],
              materials: ['Story', 'Sequence word cards', 'Picture sequences'],
              assessment: 'Can use "first" and "then" correctly',
              homework: 'Practice sequence words'
            },
            {
              day: 'Wednesday',
              title: 'Retelling Practice',
              duration: '35 min',
              objectives: ['Retell a simple story in order', 'Use sequence words'],
              introduction: 'Today we will practice telling stories ourselves!',
              mainActivities: [
                { activity: 'Teacher Model', duration: '10 min', description: 'Model retelling with sequence words' },
                { activity: 'Partner Practice', duration: '15 min', description: 'Pairs retell to each other' },
                { activity: 'Share Out', duration: '10 min', description: 'Volunteers retell to class' }
              ],
              materials: ['Story props', 'Sequence word chart'],
              assessment: 'Can retell with 3+ events in order',
              homework: 'Retell a story to family'
            },
            {
              day: 'Thursday',
              title: 'Using Expression',
              duration: '35 min',
              objectives: ['Speak with appropriate expression', 'Use voice for different characters'],
              introduction: 'Stories are more exciting when we use our voice!',
              mainActivities: [
                { activity: 'Voice Warm-up', duration: '10 min', description: 'Practice loud/soft, fast/slow, happy/sad' },
                { activity: 'Character Voices', duration: '15 min', description: 'Give characters different voices' },
                { activity: 'Expression Practice', duration: '10 min', description: 'Retell with expression' }
              ],
              materials: ['Character puppets', 'Expression cards'],
              assessment: 'Uses expression when speaking',
              homework: 'Practice character voices'
            },
            {
              day: 'Friday',
              title: 'Story Retelling Performance',
              duration: '35 min',
              objectives: ['Perform complete story retelling', 'Demonstrate all skills learned'],
              introduction: 'Today is performance day! Show what you learned.',
              mainActivities: [
                { activity: 'Preparation', duration: '10 min', description: 'Practice in pairs' },
                { activity: 'Performances', duration: '20 min', description: 'Small groups perform retellings' },
                { activity: 'Celebration', duration: '5 min', description: 'Celebrate good storytelling!' }
              ],
              materials: ['Props', 'Reward stickers'],
              assessment: 'Retelling performance rubric',
              homework: 'Tell a new story over the weekend'
            }
          ]
        },
        {
          week: 2,
          topic: 'Word Families: -at, -an, -ap',
          strand: 'Reading',
          learningOutcomes: [
            'Recognize word family patterns',
            'Read words in the -at, -an, -ap families',
            'Build new words using patterns'
          ],
          activities: [
            'Word family sorts',
            'Rhyming games',
            'Word building with letter tiles'
          ],
          resources: ['Word family charts', 'Letter tiles', 'Word cards']
        },
        {
          week: 3,
          topic: 'Simple Sentences',
          strand: 'Writing',
          learningOutcomes: [
            'Write complete simple sentences',
            'Use capital letters and full stops',
            'Write sentences about pictures'
          ],
          activities: [
            'Sentence building activities',
            'Picture prompts writing',
            'Sentence correction practice'
          ],
          resources: ['Sentence strips', 'Picture cards', 'Writing journals']
        },
        {
          week: 4,
          topic: 'Word Families: -ig, -in, -it',
          strand: 'Reading',
          learningOutcomes: [
            'Read words in the -ig, -in, -it families',
            'Identify rhyming words',
            'Sound out new words using patterns'
          ],
          activities: [
            'Word family matching',
            'Rhyme time activities',
            'Reading simple texts'
          ],
          resources: ['Word family books', 'Rhyming cards', 'Decodable readers']
        },
        {
          week: 5,
          topic: 'Following Instructions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Listen and follow 2-3 step instructions',
            'Give simple instructions to others',
            'Use sequence words for instructions'
          ],
          activities: [
            'Simon Says games',
            'Instruction relay races',
            'Art activity following steps'
          ],
          resources: ['Instruction cards', 'Craft materials', 'Game equipment']
        },
        {
          week: 6,
          topic: 'Word Families: -ot, -op, -og',
          strand: 'Reading',
          learningOutcomes: [
            'Read words in the -ot, -op, -og families',
            'Blend sounds to read words',
            'Write words from word families'
          ],
          activities: [
            'Word building activities',
            'Reading word family sentences',
            'Word family dictation'
          ],
          resources: ['Magnetic letters', 'Word family readers', 'Whiteboards']
        },
        {
          week: 7,
          topic: 'Writing About Myself',
          strand: 'Writing',
          learningOutcomes: [
            'Write sentences about self and family',
            'Use personal pronouns (I, my, me)',
            'Write 3-4 sentences on a topic'
          ],
          activities: [
            'All About Me books',
            'Family drawings with captions',
            'Sentence expansion practice'
          ],
          resources: ['Writing booklets', 'Family photo prompts', 'Word walls']
        },
        {
          week: 8,
          topic: 'Reading Pictures',
          strand: 'Viewing',
          learningOutcomes: [
            'Describe what is seen in pictures',
            'Make predictions from images',
            'Connect pictures to text'
          ],
          activities: [
            'Picture walks through books',
            'Prediction activities',
            'Picture-text matching'
          ],
          resources: ['Picture books', 'Prediction charts', 'Image cards']
        },
        {
          week: 9,
          topic: 'Word Families: -ug, -un, -ut',
          strand: 'Reading',
          learningOutcomes: [
            'Read words in the -ug, -un, -ut families',
            'Read simple sentences fluently',
            'Recognize patterns in new words'
          ],
          activities: [
            'Fluency practice',
            'Partner reading',
            'Word family games'
          ],
          resources: ['Fluency passages', 'Word family games', 'Reading partners']
        },
        {
          week: 10,
          topic: 'Term 1 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate reading progress',
            'Show understanding of word families',
            'Complete simple writing tasks'
          ],
          activities: [
            'Reading assessments',
            'Writing samples collection',
            'Oral presentations'
          ],
          resources: ['Assessment materials', 'Portfolio folders', 'Celebration supplies']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Reading Comprehension & Expanded Writing',
      scheme: [
        {
          week: 1,
          topic: 'Asking Questions About Stories',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Ask questions using who, what, where',
            'Answer questions about stories heard',
            'Participate in story discussions'
          ],
          activities: ['Question practice', 'Story discussions', 'Partner questioning'],
          resources: ['Question cards', 'Story books', 'Discussion prompts']
        },
        {
          week: 2,
          topic: 'Long Vowels: a_e, i_e',
          strand: 'Reading',
          learningOutcomes: [
            'Recognize silent e pattern',
            'Read words with long a and long i',
            'Compare short and long vowel sounds'
          ],
          activities: ['Magic e activities', 'Word sorting', 'Reading practice'],
          resources: ['Silent e word cards', 'Sorting mats', 'Decodable texts']
        },
        {
          week: 3,
          topic: 'Writing Lists and Labels',
          strand: 'Writing',
          learningOutcomes: [
            'Write simple lists',
            'Label pictures and diagrams',
            'Use capital letters for names'
          ],
          activities: ['List making', 'Labeling activities', 'Classroom labels'],
          resources: ['List templates', 'Diagram worksheets', 'Label supplies']
        },
        {
          week: 4,
          topic: 'Long Vowels: o_e, u_e',
          strand: 'Reading',
          learningOutcomes: [
            'Read words with long o and long u',
            'Apply silent e pattern consistently',
            'Read sentences with mixed vowels'
          ],
          activities: ['Vowel pattern practice', 'Sentence reading', 'Word building'],
          resources: ['Long vowel charts', 'Reading passages', 'Letter tiles']
        },
        {
          week: 5,
          topic: 'Describing People',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Use adjectives to describe people',
            'Speak in complete sentences',
            'Listen and identify described person'
          ],
          activities: ['Describing games', 'Adjective practice', 'Guessing games'],
          resources: ['Picture cards', 'Adjective word bank', 'Description frames']
        },
        {
          week: 6,
          topic: 'Reading Comprehension: Main Idea',
          strand: 'Reading',
          learningOutcomes: [
            'Identify main idea of short texts',
            'Find supporting details',
            'Summarize what was read'
          ],
          activities: ['Main idea practice', 'Detail hunting', 'Simple summaries'],
          resources: ['Comprehension passages', 'Graphic organizers', 'Summary frames']
        },
        {
          week: 7,
          topic: 'Writing Descriptions',
          strand: 'Writing',
          learningOutcomes: [
            'Write descriptive sentences',
            'Use adjectives in writing',
            'Describe people, places, things'
          ],
          activities: ['Descriptive writing', 'Adjective games', 'Description books'],
          resources: ['Description templates', 'Picture prompts', 'Adjective lists']
        },
        {
          week: 8,
          topic: 'Understanding Signs and Symbols',
          strand: 'Viewing',
          learningOutcomes: [
            'Recognize common signs',
            'Understand symbols in the environment',
            'Connect signs to their meanings'
          ],
          activities: ['Sign walks', 'Symbol matching', 'Safety sign recognition'],
          resources: ['Sign pictures', 'Symbol cards', 'Environmental print']
        },
        {
          week: 9,
          topic: 'Digraphs: ch, sh, th',
          strand: 'Reading',
          learningOutcomes: [
            'Read words with ch, sh, th',
            'Identify digraphs in words',
            'Write words with digraphs'
          ],
          activities: ['Digraph sorting', 'Sound practice', 'Digraph word building'],
          resources: ['Digraph charts', 'Word cards', 'Digraph games']
        },
        {
          week: 10,
          topic: 'Term 2 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate reading improvement',
            'Show writing development',
            'Perform oral skills'
          ],
          activities: ['Reading assessments', 'Writing portfolio review', 'Speaking tasks'],
          resources: ['Assessment materials', 'Portfolios', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Narrative Writing & Reading Fluency',
      scheme: [
        {
          week: 1,
          topic: 'Talking About Events',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Share personal experiences',
            'Use past tense correctly',
            'Listen to others\' stories'
          ],
          activities: ['Sharing circle', 'Past tense practice', 'News time'],
          resources: ['Sharing prompts', 'Verb cards', 'News recording sheets']
        },
        {
          week: 2,
          topic: 'Blends: bl, cl, fl, gl, pl',
          strand: 'Reading',
          learningOutcomes: [
            'Read words with l-blends',
            'Identify blends at beginning of words',
            'Fluently read blend words'
          ],
          activities: ['Blend practice', 'Word hunts', 'Fluency passages'],
          resources: ['Blend charts', 'Word lists', 'Reading materials']
        },
        {
          week: 3,
          topic: 'Writing Simple Stories',
          strand: 'Writing',
          learningOutcomes: [
            'Write a story with beginning, middle, end',
            'Use sequence words in writing',
            'Include characters and setting'
          ],
          activities: ['Story planning', 'Writing conferences', 'Story sharing'],
          resources: ['Story planners', 'Writing paper', 'Story starters']
        },
        {
          week: 4,
          topic: 'Blends: br, cr, dr, fr, gr, tr',
          strand: 'Reading',
          learningOutcomes: [
            'Read words with r-blends',
            'Distinguish between l-blends and r-blends',
            'Apply blend knowledge to new words'
          ],
          activities: ['Blend sorting', 'Reading games', 'Word building'],
          resources: ['Blend cards', 'Sorting mats', 'Reading games']
        },
        {
          week: 5,
          topic: 'Giving Opinions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Express likes and dislikes',
            'Give simple reasons for opinions',
            'Listen respectfully to others\' opinions'
          ],
          activities: ['Opinion sharing', 'Voting activities', 'Discussion circles'],
          resources: ['Opinion frames', 'Voting materials', 'Discussion cards']
        },
        {
          week: 6,
          topic: 'Reading for Information',
          strand: 'Reading',
          learningOutcomes: [
            'Read simple non-fiction texts',
            'Identify facts in texts',
            'Use text features (titles, pictures)'
          ],
          activities: ['Non-fiction reading', 'Fact finding', 'Text feature exploration'],
          resources: ['Non-fiction books', 'Fact recording sheets', 'Information texts']
        },
        {
          week: 7,
          topic: 'Writing Recounts',
          strand: 'Writing',
          learningOutcomes: [
            'Write about personal experiences',
            'Use time order words',
            'Include feelings and reactions'
          ],
          activities: ['Recount writing', 'Weekend news', 'Experience sharing'],
          resources: ['Recount frames', 'Time word lists', 'Writing journals']
        },
        {
          week: 8,
          topic: 'Looking at Advertisements',
          strand: 'Viewing',
          learningOutcomes: [
            'Identify purpose of advertisements',
            'Recognize persuasive images',
            'Discuss what ads want us to do'
          ],
          activities: ['Ad analysis', 'Create simple ads', 'Discussion activities'],
          resources: ['Advertisement samples', 'Art supplies', 'Discussion guides']
        },
        {
          week: 9,
          topic: 'Blends: sk, sm, sn, sp, st, sw',
          strand: 'Reading',
          learningOutcomes: [
            'Read words with s-blends',
            'Apply all blend knowledge',
            'Read fluently with expression'
          ],
          activities: ['Blend review', 'Fluency practice', 'Expression reading'],
          resources: ['Blend review materials', 'Fluency passages', 'Recording tools']
        },
        {
          week: 10,
          topic: 'Term 3 Review & Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Show reading fluency progress',
            'Demonstrate writing skills',
            'Present oral work confidently'
          ],
          activities: ['Fluency testing', 'Writing assessment', 'Presentations'],
          resources: ['Assessment tools', 'Portfolio materials', 'Presentation supplies']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Independent Reading & Creative Writing',
      scheme: [
        {
          week: 1,
          topic: 'Storytelling in Vanuatu',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Listen to traditional stories',
            'Retell cultural stories',
            'Appreciate oral traditions'
          ],
          activities: ['Guest storytellers', 'Cultural sharing', 'Story dramatization'],
          resources: ['Traditional stories', 'Storytelling props', 'Cultural materials']
        },
        {
          week: 2,
          topic: 'Compound Words',
          strand: 'Reading',
          learningOutcomes: [
            'Identify compound words',
            'Break compound words into parts',
            'Create new compound words'
          ],
          activities: ['Compound word matching', 'Word building', 'Compound word hunt'],
          resources: ['Compound word cards', 'Word lists', 'Games']
        },
        {
          week: 3,
          topic: 'Writing Letters',
          strand: 'Writing',
          learningOutcomes: [
            'Write a friendly letter',
            'Use greeting and closing',
            'Include message content'
          ],
          activities: ['Letter writing', 'Pen pal introduction', 'Letter format practice'],
          resources: ['Letter templates', 'Writing paper', 'Example letters']
        },
        {
          week: 4,
          topic: 'Reading Longer Texts',
          strand: 'Reading',
          learningOutcomes: [
            'Read chapter books independently',
            'Maintain comprehension over time',
            'Discuss books with others'
          ],
          activities: ['Independent reading', 'Book discussions', 'Reading logs'],
          resources: ['Chapter books', 'Reading logs', 'Discussion guides']
        },
        {
          week: 5,
          topic: 'Speaking About Books',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Recommend books to others',
            'Speak about favorite parts',
            'Answer questions about books'
          ],
          activities: ['Book talks', 'Partner sharing', 'Class recommendations'],
          resources: ['Book talk frames', 'Sharing guidelines', 'Recording sheets']
        },
        {
          week: 6,
          topic: 'High-Frequency Words Review',
          strand: 'Reading',
          learningOutcomes: [
            'Read all Year 2 sight words',
            'Use sight words in writing',
            'Read fluently at grade level'
          ],
          activities: ['Sight word games', 'Reading practice', 'Word wall activities'],
          resources: ['Sight word lists', 'Games', 'Assessment materials']
        },
        {
          week: 7,
          topic: 'Creative Writing',
          strand: 'Writing',
          learningOutcomes: [
            'Write imaginative stories',
            'Include creative details',
            'Develop characters and settings'
          ],
          activities: ['Creative writing time', 'Story illustrations', 'Author sharing'],
          resources: ['Story starters', 'Illustration materials', 'Publishing supplies']
        },
        {
          week: 8,
          topic: 'Visual Storytelling',
          strand: 'Viewing',
          learningOutcomes: [
            'Create visual stories',
            'Understand how images tell stories',
            'Combine pictures and words'
          ],
          activities: ['Comic creation', 'Picture stories', 'Visual presentations'],
          resources: ['Comic templates', 'Art supplies', 'Example visuals']
        },
        {
          week: 9,
          topic: 'Year 2 Reading Review',
          strand: 'Reading',
          learningOutcomes: [
            'Demonstrate all reading skills',
            'Read independently at Year 2 level',
            'Show comprehension strategies'
          ],
          activities: ['Reading marathon', 'Skills demonstration', 'Final assessments'],
          resources: ['Assessment materials', 'Reading selections', 'Recording tools']
        },
        {
          week: 10,
          topic: 'Year 2 Celebration & Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Celebrate learning achievements',
            'Complete final assessments',
            'Set goals for Year 3'
          ],
          activities: ['Awards ceremony', 'Final assessments', 'Goal setting'],
          resources: ['Certificates', 'Assessment materials', 'Goal sheets']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 3 ENGLISH CURRICULUM
// Transitioning to More Complex Texts
// ============================================
export const englishYear3 = {
  year: 3,
  subject: 'English',
  totalWeeks: 40,
  periodsPerWeek: 7,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Comprehension Strategies & Paragraph Writing',
      scheme: [
        {
          week: 1,
          topic: 'Making Predictions',
          strand: 'Reading',
          learningOutcomes: [
            'Make predictions before reading',
            'Use text clues to predict',
            'Confirm or revise predictions'
          ],
          activities: ['Cover predictions', 'Reading stops', 'Prediction journals'],
          resources: ['Prediction charts', 'Books', 'Prediction worksheets'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Predictions',
              duration: '40 min',
              objectives: ['Understand what prediction means', 'Make predictions from book covers'],
              introduction: 'What do you think will happen in this story? Today we become prediction detectives!',
              mainActivities: [
                { activity: 'Cover Study', duration: '10 min', description: 'Look at book cover, title, pictures' },
                { activity: 'Prediction Practice', duration: '20 min', description: 'Write predictions before reading' },
                { activity: 'Begin Reading', duration: '10 min', description: 'Start reading to check predictions' }
              ],
              materials: ['New books', 'Prediction worksheets', 'Pencils'],
              assessment: 'Quality of predictions made',
              homework: 'Predict what a book at home is about from its cover'
            },
            {
              day: 'Tuesday',
              title: 'Using Clues to Predict',
              duration: '40 min',
              objectives: ['Find clues in text to make predictions', 'Support predictions with evidence'],
              introduction: 'Good readers look for clues! What clues help us predict?',
              mainActivities: [
                { activity: 'Clue Hunt', duration: '15 min', description: 'Find clues in pictures and text' },
                { activity: 'Prediction Stations', duration: '15 min', description: 'Rotate through books, make predictions' },
                { activity: 'Share Predictions', duration: '10 min', description: 'Share and discuss predictions' }
              ],
              materials: ['Various books', 'Clue recording sheets', 'Station materials'],
              assessment: 'Can identify clues for predictions',
              homework: 'Find 3 clues in your reading book'
            },
            {
              day: 'Wednesday',
              title: 'Checking Predictions',
              duration: '40 min',
              objectives: ['Check predictions against what happens', 'Revise predictions as needed'],
              introduction: 'Were our predictions right? Let\'s find out!',
              mainActivities: [
                { activity: 'Continue Reading', duration: '15 min', description: 'Read more of the story' },
                { activity: 'Prediction Check', duration: '15 min', description: 'Were predictions correct? Why or why not?' },
                { activity: 'New Predictions', duration: '10 min', description: 'Make new predictions for ending' }
              ],
              materials: ['Class book', 'Prediction tracking charts'],
              assessment: 'Can evaluate own predictions',
              homework: 'Continue reading, check your predictions'
            },
            {
              day: 'Thursday',
              title: 'Prediction Strategies',
              duration: '40 min',
              objectives: ['Use multiple strategies to predict', 'Apply strategies independently'],
              introduction: 'What strategies help us make good predictions?',
              mainActivities: [
                { activity: 'Strategy Review', duration: '10 min', description: 'Review all prediction strategies' },
                { activity: 'Independent Practice', duration: '20 min', description: 'Apply strategies to new text' },
                { activity: 'Partner Share', duration: '10 min', description: 'Discuss predictions with partner' }
              ],
              materials: ['Strategy chart', 'Independent reading books'],
              assessment: 'Uses multiple prediction strategies',
              homework: 'Practice predicting while reading'
            },
            {
              day: 'Friday',
              title: 'Prediction Presentations',
              duration: '40 min',
              objectives: ['Present predictions to class', 'Explain reasoning'],
              introduction: 'Share your prediction detective work!',
              mainActivities: [
                { activity: 'Prepare Presentations', duration: '10 min', description: 'Organize prediction work' },
                { activity: 'Presentations', duration: '25 min', description: 'Share predictions and outcomes' },
                { activity: 'Celebration', duration: '5 min', description: 'Celebrate prediction skills' }
              ],
              materials: ['Student work', 'Presentation area'],
              assessment: 'Presentation quality',
              homework: 'Make predictions for weekend reading'
            }
          ]
        },
        {
          week: 2,
          topic: 'Oral Presentations',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Prepare a short presentation',
            'Speak clearly to a group',
            'Make eye contact when speaking'
          ],
          activities: ['Presentation practice', 'Feedback sessions', 'Speaking circles'],
          resources: ['Presentation rubrics', 'Topic cards', 'Timer']
        },
        {
          week: 3,
          topic: 'Paragraph Structure',
          strand: 'Writing',
          learningOutcomes: [
            'Understand paragraph structure',
            'Write topic sentences',
            'Include supporting details'
          ],
          activities: ['Paragraph analysis', 'Topic sentence practice', 'Supporting detail work'],
          resources: ['Paragraph examples', 'Writing frames', 'Graphic organizers']
        },
        {
          week: 4,
          topic: 'Making Connections',
          strand: 'Reading',
          learningOutcomes: [
            'Connect text to self',
            'Connect text to other texts',
            'Connect text to world'
          ],
          activities: ['Connection charts', 'Discussion groups', 'Connection journals'],
          resources: ['Connection frames', 'Books', 'Journal materials']
        },
        {
          week: 5,
          topic: 'Discussion Skills',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Participate in group discussions',
            'Take turns speaking',
            'Build on others\' ideas'
          ],
          activities: ['Discussion circles', 'Turn-taking practice', 'Idea building'],
          resources: ['Discussion guidelines', 'Talking chips', 'Question cards']
        },
        {
          week: 6,
          topic: 'Prefixes and Suffixes',
          strand: 'Reading',
          learningOutcomes: [
            'Identify common prefixes (un-, re-)',
            'Identify common suffixes (-ing, -ed, -er)',
            'Use word parts to determine meaning'
          ],
          activities: ['Word building', 'Affix sorting', 'Meaning practice'],
          resources: ['Affix charts', 'Word cards', 'Worksheets']
        },
        {
          week: 7,
          topic: 'Writing Complete Paragraphs',
          strand: 'Writing',
          learningOutcomes: [
            'Write organized paragraphs',
            'Use transition words',
            'Write concluding sentences'
          ],
          activities: ['Guided paragraph writing', 'Peer editing', 'Publishing'],
          resources: ['Paragraph templates', 'Transition word lists', 'Editing checklists']
        },
        {
          week: 8,
          topic: 'Visual Texts',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze charts and diagrams',
            'Read maps and graphs',
            'Extract information from visuals'
          ],
          activities: ['Chart reading', 'Map activities', 'Graph interpretation'],
          resources: ['Various charts/graphs', 'Maps', 'Information worksheets']
        },
        {
          week: 9,
          topic: 'Fluency Development',
          strand: 'Reading',
          learningOutcomes: [
            'Read with appropriate speed',
            'Use expression and phrasing',
            'Self-correct when reading'
          ],
          activities: ['Repeated reading', 'Partner reading', 'Reader\'s theater'],
          resources: ['Fluency passages', 'Scripts', 'Recording tools']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate comprehension skills',
            'Show writing development',
            'Display speaking confidence'
          ],
          activities: ['Reading assessments', 'Writing samples', 'Oral presentations'],
          resources: ['Assessment materials', 'Rubrics', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Narrative Elements & Extended Writing',
      scheme: [
        {
          week: 1,
          topic: 'Character Development',
          strand: 'Reading',
          learningOutcomes: [
            'Identify character traits',
            'Find evidence of traits in text',
            'Understand character motivation'
          ],
          activities: ['Character webs', 'Evidence hunting', 'Character discussions'],
          resources: ['Character graphic organizers', 'Books', 'Evidence frames']
        },
        {
          week: 2,
          topic: 'Interviewing Skills',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Prepare interview questions',
            'Conduct simple interviews',
            'Listen and respond appropriately'
          ],
          activities: ['Question writing', 'Practice interviews', 'Interview presentations'],
          resources: ['Question frames', 'Recording sheets', 'Interview guidelines']
        },
        {
          week: 3,
          topic: 'Story Writing: Beginning',
          strand: 'Writing',
          learningOutcomes: [
            'Write engaging story openings',
            'Introduce characters and setting',
            'Hook the reader\'s attention'
          ],
          activities: ['Opening analysis', 'Hook practice', 'Story starts'],
          resources: ['Opening examples', 'Writing frames', 'Story starters']
        },
        {
          week: 4,
          topic: 'Plot and Setting',
          strand: 'Reading',
          learningOutcomes: [
            'Identify main events in plot',
            'Describe settings in detail',
            'Understand how setting affects story'
          ],
          activities: ['Plot mapping', 'Setting descriptions', 'Comparison activities'],
          resources: ['Plot diagrams', 'Setting worksheets', 'Books']
        },
        {
          week: 5,
          topic: 'Formal and Informal Language',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Distinguish formal and informal language',
            'Use appropriate register',
            'Adapt language for audience'
          ],
          activities: ['Register sorting', 'Role play', 'Language matching'],
          resources: ['Language examples', 'Role play cards', 'Sorting materials']
        },
        {
          week: 6,
          topic: 'Vocabulary Expansion',
          strand: 'Reading',
          learningOutcomes: [
            'Use context clues for unknown words',
            'Learn and use new vocabulary',
            'Keep a personal word bank'
          ],
          activities: ['Context clue practice', 'Vocabulary games', 'Word collection'],
          resources: ['Vocabulary journals', 'Word games', 'Context practice texts']
        },
        {
          week: 7,
          topic: 'Story Writing: Middle and End',
          strand: 'Writing',
          learningOutcomes: [
            'Develop story problems and solutions',
            'Build to a climax',
            'Write satisfying endings'
          ],
          activities: ['Problem-solution writing', 'Climax practice', 'Ending workshops'],
          resources: ['Story structure guides', 'Writing paper', 'Example endings']
        },
        {
          week: 8,
          topic: 'Advertisements and Persuasion',
          strand: 'Viewing',
          learningOutcomes: [
            'Identify persuasive techniques',
            'Analyze advertisement messages',
            'Create simple advertisements'
          ],
          activities: ['Ad analysis', 'Technique identification', 'Ad creation'],
          resources: ['Advertisement samples', 'Analysis worksheets', 'Art supplies']
        },
        {
          week: 9,
          topic: 'Reading Fluency and Expression',
          strand: 'Reading',
          learningOutcomes: [
            'Read with appropriate expression',
            'Use punctuation to guide reading',
            'Perform fluent oral reading'
          ],
          activities: ['Expression practice', 'Punctuation reading', 'Performance reading'],
          resources: ['Fluency passages', 'Scripts', 'Recording equipment']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Show progress in all strands',
            'Complete narrative writing',
            'Present oral work'
          ],
          activities: ['Portfolio review', 'Writing assessment', 'Presentations'],
          resources: ['Assessment materials', 'Portfolios', 'Rubrics']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Information Texts & Report Writing',
      scheme: [
        {
          week: 1,
          topic: 'Research Skills',
          strand: 'Reading',
          learningOutcomes: [
            'Use contents and index pages',
            'Locate information in texts',
            'Take simple notes'
          ],
          activities: ['Book navigation', 'Note-taking practice', 'Research tasks'],
          resources: ['Non-fiction books', 'Note-taking templates', 'Research guides']
        },
        {
          week: 2,
          topic: 'Reporting Information',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Report findings to class',
            'Organize information for presentation',
            'Answer questions about topic'
          ],
          activities: ['Mini reports', 'Q&A practice', 'Presentation skills'],
          resources: ['Report frames', 'Presentation guidelines', 'Question cards']
        },
        {
          week: 3,
          topic: 'Information Report Structure',
          strand: 'Writing',
          learningOutcomes: [
            'Understand report structure',
            'Write clear topic sentences',
            'Organize facts logically'
          ],
          activities: ['Report analysis', 'Structure practice', 'Fact organization'],
          resources: ['Report examples', 'Structure templates', 'Fact cards']
        },
        {
          week: 4,
          topic: 'Comparing Texts',
          strand: 'Reading',
          learningOutcomes: [
            'Compare different texts on same topic',
            'Identify similarities and differences',
            'Evaluate text usefulness'
          ],
          activities: ['Text comparison', 'Venn diagrams', 'Source evaluation'],
          resources: ['Multiple texts', 'Comparison organizers', 'Evaluation guides']
        },
        {
          week: 5,
          topic: 'Debate and Discussion',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Present a point of view',
            'Support opinions with reasons',
            'Respond to opposing views respectfully'
          ],
          activities: ['Mini debates', 'Opinion presentations', 'Discussion practice'],
          resources: ['Debate topics', 'Opinion frames', 'Discussion guidelines']
        },
        {
          week: 6,
          topic: 'Dictionary and Reference Skills',
          strand: 'Reading',
          learningOutcomes: [
            'Use a dictionary effectively',
            'Find word meanings and spellings',
            'Use a thesaurus for synonyms'
          ],
          activities: ['Dictionary races', 'Word lookup practice', 'Thesaurus exploration'],
          resources: ['Dictionaries', 'Thesauruses', 'Worksheets']
        },
        {
          week: 7,
          topic: 'Writing Information Reports',
          strand: 'Writing',
          learningOutcomes: [
            'Write complete information reports',
            'Include introduction and conclusion',
            'Use facts and details'
          ],
          activities: ['Report drafting', 'Peer editing', 'Report publishing'],
          resources: ['Report templates', 'Editing checklists', 'Publishing materials']
        },
        {
          week: 8,
          topic: 'Understanding Diagrams',
          strand: 'Viewing',
          learningOutcomes: [
            'Read and interpret diagrams',
            'Create labeled diagrams',
            'Extract information from diagrams'
          ],
          activities: ['Diagram analysis', 'Labeling practice', 'Diagram creation'],
          resources: ['Various diagrams', 'Labeling templates', 'Drawing materials']
        },
        {
          week: 9,
          topic: 'Genre Study: Non-Fiction',
          strand: 'Reading',
          learningOutcomes: [
            'Identify features of non-fiction',
            'Compare fiction and non-fiction',
            'Choose appropriate texts for purposes'
          ],
          activities: ['Genre sorting', 'Feature identification', 'Genre comparison'],
          resources: ['Various genre books', 'Feature charts', 'Sorting activities']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate research skills',
            'Show report writing ability',
            'Present information orally'
          ],
          activities: ['Research presentations', 'Writing assessment', 'Skills testing'],
          resources: ['Assessment materials', 'Rubrics', 'Recording tools']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Poetry & Creative Expression',
      scheme: [
        {
          week: 1,
          topic: 'Poetry Appreciation',
          strand: 'Reading',
          learningOutcomes: [
            'Identify features of poetry',
            'Appreciate rhythm and rhyme',
            'Respond to poems emotionally'
          ],
          activities: ['Poetry reading', 'Rhythm activities', 'Poetry responses'],
          resources: ['Poetry collections', 'Response journals', 'Audio recordings']
        },
        {
          week: 2,
          topic: 'Performing Poetry',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Read poetry with expression',
            'Use voice effectively for performance',
            'Memorize short poems'
          ],
          activities: ['Choral reading', 'Performance practice', 'Poetry recital'],
          resources: ['Poems for performance', 'Recording equipment', 'Performance space']
        },
        {
          week: 3,
          topic: 'Writing Poetry',
          strand: 'Writing',
          learningOutcomes: [
            'Write simple poems',
            'Use descriptive language',
            'Experiment with forms'
          ],
          activities: ['Acrostic poems', 'Haiku writing', 'Free verse'],
          resources: ['Poetry frames', 'Word banks', 'Publishing materials']
        },
        {
          week: 4,
          topic: 'Author Study',
          strand: 'Reading',
          learningOutcomes: [
            'Study works by one author',
            'Identify author\'s style',
            'Compare multiple works'
          ],
          activities: ['Author reading', 'Style analysis', 'Author comparisons'],
          resources: ['Author book sets', 'Author information', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Dramatization',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Participate in drama activities',
            'Use voice and movement for character',
            'Work cooperatively in groups'
          ],
          activities: ['Role play', 'Story dramatization', 'Mini performances'],
          resources: ['Scripts', 'Props', 'Performance space']
        },
        {
          week: 6,
          topic: 'Reading Comprehension Review',
          strand: 'Reading',
          learningOutcomes: [
            'Apply all comprehension strategies',
            'Read independently at Year 3 level',
            'Demonstrate reading growth'
          ],
          activities: ['Strategy application', 'Independent reading', 'Comprehension checks'],
          resources: ['Reading materials', 'Strategy cards', 'Assessment tools']
        },
        {
          week: 7,
          topic: 'Creative Writing Celebration',
          strand: 'Writing',
          learningOutcomes: [
            'Publish best writing pieces',
            'Celebrate writing growth',
            'Share work with others'
          ],
          activities: ['Publishing party', 'Author\'s chair', 'Writing celebration'],
          resources: ['Publishing materials', 'Celebration supplies', 'Display boards']
        },
        {
          week: 8,
          topic: 'Film and Media',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze visual storytelling',
            'Identify camera techniques',
            'Compare book and film versions'
          ],
          activities: ['Film viewing', 'Comparison activities', 'Discussion'],
          resources: ['Short films', 'Comparison worksheets', 'Discussion guides']
        },
        {
          week: 9,
          topic: 'Year 3 Review',
          strand: 'All Strands',
          learningOutcomes: [
            'Review all Year 3 learning',
            'Identify strengths and growth areas',
            'Prepare for Year 4'
          ],
          activities: ['Skills review', 'Portfolio completion', 'Goal setting'],
          resources: ['Review materials', 'Portfolios', 'Goal worksheets']
        },
        {
          week: 10,
          topic: 'Year 3 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Complete final assessments',
            'Celebrate Year 3 achievements',
            'Transition to Year 4'
          ],
          activities: ['Final testing', 'Awards ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 4 ENGLISH CURRICULUM
// Developing Independence in Reading & Writing
// ============================================
export const englishYear4 = {
  year: 4,
  subject: 'English',
  totalWeeks: 40,
  periodsPerWeek: 7,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Narrative Writing & Inference Skills',
      scheme: [
        {
          week: 1,
          topic: 'Making Inferences',
          strand: 'Reading',
          learningOutcomes: [
            'Make inferences from text clues',
            'Support inferences with evidence',
            'Distinguish inference from prediction'
          ],
          activities: ['Inference practice', 'Evidence hunting', 'Inference games'],
          resources: ['Inference passages', 'Evidence frames', 'Games'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'What is an Inference?',
              duration: '45 min',
              objectives: ['Define inference', 'Understand how to make inferences'],
              introduction: 'Sometimes authors don\'t tell us everything directly. We have to figure things out!',
              mainActivities: [
                { activity: 'Definition and Examples', duration: '15 min', description: 'Explain inference with everyday examples' },
                { activity: 'Picture Inferences', duration: '20 min', description: 'Make inferences from images' },
                { activity: 'Practice Activity', duration: '10 min', description: 'Text-based inference practice' }
              ],
              materials: ['Pictures', 'Inference worksheets', 'Whiteboard'],
              assessment: 'Can explain what inference means',
              homework: 'Find 3 things you can infer from a picture'
            },
            {
              day: 'Tuesday',
              title: 'Text Clues for Inference',
              duration: '45 min',
              objectives: ['Find clues in text', 'Use clues to make inferences'],
              introduction: 'Authors leave clues for us. Let\'s become detectives!',
              mainActivities: [
                { activity: 'Clue Highlighting', duration: '15 min', description: 'Highlight clues in short passages' },
                { activity: 'Inference Practice', duration: '20 min', description: 'Make inferences from clues' },
                { activity: 'Partner Discussion', duration: '10 min', description: 'Share inferences with partner' }
              ],
              materials: ['Passages', 'Highlighters', 'Inference frames'],
              assessment: 'Identifies relevant clues',
              homework: 'Find clues in your reading book'
            },
            {
              day: 'Wednesday',
              title: 'Supporting Inferences with Evidence',
              duration: '45 min',
              objectives: ['Provide evidence for inferences', 'Explain reasoning'],
              introduction: 'Good readers can explain WHY they think something.',
              mainActivities: [
                { activity: 'Evidence Models', duration: '15 min', description: 'Model supporting inferences with evidence' },
                { activity: 'Evidence Practice', duration: '20 min', description: 'Practice adding evidence to inferences' },
                { activity: 'Group Share', duration: '10 min', description: 'Share evidence-based inferences' }
              ],
              materials: ['Evidence frames', 'Practice passages'],
              assessment: 'Provides appropriate evidence',
              homework: 'Write an inference with evidence'
            },
            {
              day: 'Thursday',
              title: 'Inference vs Prediction',
              duration: '45 min',
              objectives: ['Distinguish inference from prediction', 'Apply both skills'],
              introduction: 'Both use clues, but they\'re different. Let\'s explore!',
              mainActivities: [
                { activity: 'Comparison', duration: '15 min', description: 'Compare inference and prediction' },
                { activity: 'Sorting Activity', duration: '20 min', description: 'Sort statements into inference/prediction' },
                { activity: 'Application', duration: '10 min', description: 'Apply both to new text' }
              ],
              materials: ['Comparison chart', 'Sorting cards', 'New text'],
              assessment: 'Distinguishes between the two',
              homework: 'Practice both skills while reading'
            },
            {
              day: 'Friday',
              title: 'Inference Assessment',
              duration: '45 min',
              objectives: ['Demonstrate inference skills', 'Apply learning independently'],
              introduction: 'Show what you\'ve learned about making inferences!',
              mainActivities: [
                { activity: 'Review', duration: '10 min', description: 'Quick review of inference skills' },
                { activity: 'Independent Practice', duration: '25 min', description: 'Complete inference assessment' },
                { activity: 'Reflection', duration: '10 min', description: 'Reflect on learning' }
              ],
              materials: ['Assessment materials', 'Reflection journals'],
              assessment: 'Inference assessment completion',
              homework: 'Continue practicing inference skills'
            }
          ]
        },
        {
          week: 2,
          topic: 'Oral Reporting',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Prepare oral reports on topics',
            'Use notes to guide speaking',
            'Speak clearly and confidently'
          ],
          activities: ['Report preparation', 'Practice sessions', 'Presentations'],
          resources: ['Report templates', 'Note cards', 'Rubrics']
        },
        {
          week: 3,
          topic: 'Narrative Writing: Character and Setting',
          strand: 'Writing',
          learningOutcomes: [
            'Create interesting characters',
            'Describe settings vividly',
            'Use dialogue effectively'
          ],
          activities: ['Character development', 'Setting descriptions', 'Dialogue practice'],
          resources: ['Character templates', 'Setting images', 'Dialogue rules']
        },
        {
          week: 4,
          topic: 'Understanding Theme',
          strand: 'Reading',
          learningOutcomes: [
            'Identify themes in stories',
            'Support theme with evidence',
            'Compare themes across texts'
          ],
          activities: ['Theme identification', 'Evidence gathering', 'Theme comparison'],
          resources: ['Theme lists', 'Evidence charts', 'Multiple texts']
        },
        {
          week: 5,
          topic: 'Group Discussions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Lead small group discussions',
            'Stay on topic in discussions',
            'Summarize discussion points'
          ],
          activities: ['Discussion leading', 'Topic tracking', 'Summary practice'],
          resources: ['Discussion protocols', 'Summary frames', 'Group materials']
        },
        {
          week: 6,
          topic: 'Synonyms and Antonyms',
          strand: 'Reading',
          learningOutcomes: [
            'Identify synonyms for words',
            'Identify antonyms for words',
            'Use synonyms to improve writing'
          ],
          activities: ['Synonym/antonym matching', 'Word replacement', 'Writing improvement'],
          resources: ['Thesaurus', 'Word cards', 'Writing samples']
        },
        {
          week: 7,
          topic: 'Narrative Writing: Plot Development',
          strand: 'Writing',
          learningOutcomes: [
            'Develop engaging plots',
            'Create problems and solutions',
            'Build suspense in writing'
          ],
          activities: ['Plot planning', 'Problem development', 'Suspense techniques'],
          resources: ['Plot diagrams', 'Writing guides', 'Example texts']
        },
        {
          week: 8,
          topic: 'Analyzing Visual Media',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze visual elements in media',
            'Identify purpose of visual choices',
            'Create visual presentations'
          ],
          activities: ['Media analysis', 'Purpose identification', 'Presentation creation'],
          resources: ['Media samples', 'Analysis guides', 'Presentation materials']
        },
        {
          week: 9,
          topic: 'Reading Fluency',
          strand: 'Reading',
          learningOutcomes: [
            'Read fluently at Year 4 level',
            'Use appropriate pacing',
            'Self-monitor comprehension'
          ],
          activities: ['Fluency practice', 'Pacing exercises', 'Comprehension checks'],
          resources: ['Fluency materials', 'Timers', 'Comprehension questions']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate comprehension skills',
            'Show narrative writing ability',
            'Present information orally'
          ],
          activities: ['Reading tests', 'Writing assessment', 'Oral presentations'],
          resources: ['Assessment materials', 'Rubrics', 'Recording sheets']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Persuasive Writing & Critical Reading',
      scheme: [
        {
          week: 1,
          topic: 'Fact vs Opinion',
          strand: 'Reading',
          learningOutcomes: [
            'Distinguish facts from opinions',
            'Identify facts in texts',
            'Recognize opinion indicators'
          ],
          activities: ['Sorting activities', 'Fact finding', 'Opinion identification'],
          resources: ['Fact/opinion cards', 'Sorting mats', 'Practice texts']
        },
        {
          week: 2,
          topic: 'Persuasive Speaking',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Present persuasive arguments',
            'Use persuasive language',
            'Support opinions with reasons'
          ],
          activities: ['Argument practice', 'Persuasive speeches', 'Peer feedback'],
          resources: ['Speech templates', 'Persuasive language lists', 'Feedback forms']
        },
        {
          week: 3,
          topic: 'Persuasive Writing Structure',
          strand: 'Writing',
          learningOutcomes: [
            'Understand persuasive text structure',
            'Write clear thesis statements',
            'Organize persuasive arguments'
          ],
          activities: ['Structure analysis', 'Thesis writing', 'Argument organization'],
          resources: ['Persuasive examples', 'Structure templates', 'Organizers']
        },
        {
          week: 4,
          topic: 'Author\'s Purpose',
          strand: 'Reading',
          learningOutcomes: [
            'Identify author\'s purpose (inform, persuade, entertain)',
            'Find evidence of purpose',
            'Evaluate effectiveness'
          ],
          activities: ['Purpose identification', 'Evidence gathering', 'Effectiveness evaluation'],
          resources: ['Various texts', 'Purpose charts', 'Evaluation guides']
        },
        {
          week: 5,
          topic: 'Formal Debate',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Participate in structured debates',
            'Present for and against arguments',
            'Respond to opposing views'
          ],
          activities: ['Debate preparation', 'Mini debates', 'Rebuttal practice'],
          resources: ['Debate topics', 'Argument frames', 'Debate rules']
        },
        {
          week: 6,
          topic: 'Point of View',
          strand: 'Reading',
          learningOutcomes: [
            'Identify first and third person',
            'Understand narrator\'s perspective',
            'Compare different points of view'
          ],
          activities: ['Point of view identification', 'Perspective comparison', 'Rewriting activities'],
          resources: ['Various texts', 'POV charts', 'Rewriting templates']
        },
        {
          week: 7,
          topic: 'Writing Persuasive Texts',
          strand: 'Writing',
          learningOutcomes: [
            'Write complete persuasive texts',
            'Use persuasive techniques',
            'Include counterarguments'
          ],
          activities: ['Persuasive writing', 'Technique application', 'Peer review'],
          resources: ['Writing templates', 'Technique lists', 'Peer review guides']
        },
        {
          week: 8,
          topic: 'Advertising Analysis',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze advertising techniques',
            'Identify target audience',
            'Evaluate advertisement effectiveness'
          ],
          activities: ['Ad analysis', 'Audience identification', 'Effectiveness evaluation'],
          resources: ['Advertisements', 'Analysis worksheets', 'Evaluation criteria']
        },
        {
          week: 9,
          topic: 'Critical Reading',
          strand: 'Reading',
          learningOutcomes: [
            'Question text information',
            'Evaluate source reliability',
            'Form personal opinions'
          ],
          activities: ['Questioning practice', 'Source evaluation', 'Opinion formation'],
          resources: ['Various sources', 'Questioning frames', 'Evaluation checklists']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate persuasive skills',
            'Show critical reading ability',
            'Present persuasive arguments'
          ],
          activities: ['Writing assessment', 'Reading tests', 'Debate performance'],
          resources: ['Assessment materials', 'Rubrics', 'Recording tools']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Explanation Texts & Research Skills',
      scheme: [
        {
          week: 1,
          topic: 'Understanding Explanations',
          strand: 'Reading',
          learningOutcomes: [
            'Identify features of explanation texts',
            'Follow explanation sequences',
            'Understand cause and effect'
          ],
          activities: ['Text analysis', 'Sequence mapping', 'Cause-effect identification'],
          resources: ['Explanation texts', 'Sequence charts', 'Cause-effect organizers']
        },
        {
          week: 2,
          topic: 'Explanatory Speaking',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Explain processes clearly',
            'Use sequence words',
            'Answer clarifying questions'
          ],
          activities: ['Process explanations', 'Sequence practice', 'Q&A sessions'],
          resources: ['Sequence word lists', 'Process cards', 'Question prompts']
        },
        {
          week: 3,
          topic: 'Writing Explanation Texts',
          strand: 'Writing',
          learningOutcomes: [
            'Write explanation texts',
            'Use technical vocabulary',
            'Include diagrams and visuals'
          ],
          activities: ['Explanation writing', 'Vocabulary development', 'Visual creation'],
          resources: ['Explanation templates', 'Vocabulary lists', 'Diagram materials']
        },
        {
          week: 4,
          topic: 'Research Skills',
          strand: 'Reading',
          learningOutcomes: [
            'Use multiple sources for research',
            'Take organized notes',
            'Avoid plagiarism'
          ],
          activities: ['Multi-source research', 'Note-taking practice', 'Paraphrasing'],
          resources: ['Research sources', 'Note templates', 'Paraphrasing guides']
        },
        {
          week: 5,
          topic: 'Interview Techniques',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Conduct research interviews',
            'Record information accurately',
            'Follow up on answers'
          ],
          activities: ['Interview practice', 'Note-taking', 'Follow-up questions'],
          resources: ['Interview guides', 'Recording materials', 'Question banks']
        },
        {
          week: 6,
          topic: 'Text Structures',
          strand: 'Reading',
          learningOutcomes: [
            'Identify different text structures',
            'Use structure to aid comprehension',
            'Compare structures'
          ],
          activities: ['Structure identification', 'Comprehension practice', 'Structure comparison'],
          resources: ['Various text types', 'Structure charts', 'Comparison guides']
        },
        {
          week: 7,
          topic: 'Research Report Writing',
          strand: 'Writing',
          learningOutcomes: [
            'Write research reports',
            'Cite sources appropriately',
            'Include bibliography'
          ],
          activities: ['Report writing', 'Citation practice', 'Bibliography creation'],
          resources: ['Report templates', 'Citation guides', 'Bibliography examples']
        },
        {
          week: 8,
          topic: 'Information Graphics',
          strand: 'Viewing',
          learningOutcomes: [
            'Create information graphics',
            'Present data visually',
            'Interpret complex graphics'
          ],
          activities: ['Graphic creation', 'Data presentation', 'Graphic interpretation'],
          resources: ['Graphic templates', 'Data sets', 'Complex graphics']
        },
        {
          week: 9,
          topic: 'Independent Reading',
          strand: 'Reading',
          learningOutcomes: [
            'Read independently for extended periods',
            'Apply comprehension strategies',
            'Respond to reading thoughtfully'
          ],
          activities: ['Independent reading', 'Strategy application', 'Reading responses'],
          resources: ['Book selection', 'Response journals', 'Strategy cards']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate explanation writing',
            'Show research skills',
            'Present research findings'
          ],
          activities: ['Writing assessment', 'Research project', 'Presentations'],
          resources: ['Assessment materials', 'Project guidelines', 'Presentation equipment']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Literature Study & Creative Expression',
      scheme: [
        {
          week: 1,
          topic: 'Novel Study Introduction',
          strand: 'Reading',
          learningOutcomes: [
            'Read and respond to novels',
            'Track plot development',
            'Analyze characters over time'
          ],
          activities: ['Novel reading', 'Plot tracking', 'Character journals'],
          resources: ['Class novels', 'Reading logs', 'Character charts']
        },
        {
          week: 2,
          topic: 'Book Discussions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Lead literature discussions',
            'Support opinions with text evidence',
            'Build on others\' ideas'
          ],
          activities: ['Literature circles', 'Evidence citing', 'Discussion protocols'],
          resources: ['Discussion guides', 'Evidence frames', 'Protocol cards']
        },
        {
          week: 3,
          topic: 'Creative Writing: Short Stories',
          strand: 'Writing',
          learningOutcomes: [
            'Write original short stories',
            'Develop complex characters',
            'Create engaging plots'
          ],
          activities: ['Story planning', 'Character development', 'Plot creation'],
          resources: ['Planning templates', 'Character guides', 'Plot outlines']
        },
        {
          week: 4,
          topic: 'Literary Elements Analysis',
          strand: 'Reading',
          learningOutcomes: [
            'Identify literary elements',
            'Analyze figurative language',
            'Understand symbolism'
          ],
          activities: ['Element identification', 'Figurative language study', 'Symbol analysis'],
          resources: ['Literary element charts', 'Example texts', 'Analysis guides']
        },
        {
          week: 5,
          topic: 'Performance and Drama',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Perform scripted drama',
            'Use voice and body effectively',
            'Collaborate in performances'
          ],
          activities: ['Script reading', 'Performance practice', 'Group productions'],
          resources: ['Scripts', 'Props', 'Performance space']
        },
        {
          week: 6,
          topic: 'Poetry Study',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze poetry deeply',
            'Identify poetic devices',
            'Appreciate poetry forms'
          ],
          activities: ['Poetry analysis', 'Device identification', 'Form exploration'],
          resources: ['Poetry anthology', 'Device charts', 'Form examples']
        },
        {
          week: 7,
          topic: 'Writing Poetry',
          strand: 'Writing',
          learningOutcomes: [
            'Write various poetry forms',
            'Use poetic devices',
            'Express ideas creatively'
          ],
          activities: ['Poetry writing', 'Device practice', 'Creative expression'],
          resources: ['Poetry templates', 'Device guides', 'Publishing materials']
        },
        {
          week: 8,
          topic: 'Film Study',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze film techniques',
            'Compare books and films',
            'Evaluate adaptations'
          ],
          activities: ['Film viewing', 'Technique analysis', 'Comparison writing'],
          resources: ['Films', 'Analysis worksheets', 'Comparison charts']
        },
        {
          week: 9,
          topic: 'Year 4 Review',
          strand: 'All Strands',
          learningOutcomes: [
            'Review all Year 4 skills',
            'Identify areas of growth',
            'Prepare for Year 5'
          ],
          activities: ['Skills review', 'Growth reflection', 'Goal setting'],
          resources: ['Review materials', 'Reflection journals', 'Goal sheets']
        },
        {
          week: 10,
          topic: 'Year 4 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Complete final assessments',
            'Celebrate achievements',
            'Transition to Year 5'
          ],
          activities: ['Final tests', 'Awards ceremony', 'Celebration'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

// ============================================
// YEAR 5 ENGLISH CURRICULUM
// Advanced Literacy Skills Development
// ============================================
export const englishYear5 = {
  year: 5,
  subject: 'English',
  totalWeeks: 40,
  periodsPerWeek: 8,
  terms: [
    {
      term: 'Term 1',
      weeks: 10,
      focus: 'Advanced Comprehension & Essay Writing',
      scheme: [
        {
          week: 1,
          topic: 'Analyzing Arguments',
          strand: 'Reading',
          learningOutcomes: [
            'Identify argument structures',
            'Evaluate strength of evidence',
            'Recognize logical fallacies'
          ],
          activities: ['Argument analysis', 'Evidence evaluation', 'Fallacy identification'],
          resources: ['Argument texts', 'Evaluation rubrics', 'Fallacy guides'],
          dailyLessons: [
            {
              day: 'Monday',
              title: 'Introduction to Arguments',
              duration: '50 min',
              objectives: ['Understand what makes an argument', 'Identify claims and evidence'],
              introduction: 'Good readers can identify and evaluate arguments. Today we become argument detectives!',
              mainActivities: [
                { activity: 'Argument Definition', duration: '15 min', description: 'Define arguments, claims, and evidence' },
                { activity: 'Argument Hunt', duration: '25 min', description: 'Find arguments in various texts' },
                { activity: 'Discussion', duration: '10 min', description: 'Discuss findings as class' }
              ],
              materials: ['Argument texts', 'Highlighters', 'Analysis sheets'],
              assessment: 'Can identify claim and evidence',
              homework: 'Find an argument in news or media'
            },
            {
              day: 'Tuesday',
              title: 'Evaluating Evidence',
              duration: '50 min',
              objectives: ['Evaluate strength of evidence', 'Distinguish strong and weak evidence'],
              introduction: 'Not all evidence is equal. Let\'s learn to evaluate!',
              mainActivities: [
                { activity: 'Evidence Types', duration: '15 min', description: 'Explore types of evidence (facts, statistics, expert opinion)' },
                { activity: 'Evidence Ranking', duration: '25 min', description: 'Rank evidence from strongest to weakest' },
                { activity: 'Application', duration: '10 min', description: 'Apply to new arguments' }
              ],
              materials: ['Evidence cards', 'Ranking templates', 'New arguments'],
              assessment: 'Can evaluate evidence strength',
              homework: 'Evaluate evidence in an advertisement'
            },
            {
              day: 'Wednesday',
              title: 'Introduction to Logical Fallacies',
              duration: '50 min',
              objectives: ['Learn common logical fallacies', 'Recognize fallacies in arguments'],
              introduction: 'Sometimes arguments seem good but aren\'t. Let\'s learn to spot tricks!',
              mainActivities: [
                { activity: 'Fallacy Introduction', duration: '20 min', description: 'Learn 5 common fallacies' },
                { activity: 'Fallacy Spotting', duration: '20 min', description: 'Find fallacies in examples' },
                { activity: 'Game', duration: '10 min', description: 'Fallacy identification game' }
              ],
              materials: ['Fallacy cards', 'Example texts', 'Game materials'],
              assessment: 'Can identify common fallacies',
              homework: 'Find a fallacy in media'
            },
            {
              day: 'Thursday',
              title: 'Building Strong Arguments',
              duration: '50 min',
              objectives: ['Apply learning to build arguments', 'Avoid fallacies in own arguments'],
              introduction: 'Now let\'s build our own strong arguments!',
              mainActivities: [
                { activity: 'Argument Planning', duration: '15 min', description: 'Plan strong arguments' },
                { activity: 'Writing Arguments', duration: '25 min', description: 'Write short arguments' },
                { activity: 'Peer Review', duration: '10 min', description: 'Partners check for fallacies' }
              ],
              materials: ['Planning templates', 'Writing paper', 'Peer review guides'],
              assessment: 'Writes argument without fallacies',
              homework: 'Complete argument draft'
            },
            {
              day: 'Friday',
              title: 'Argument Presentations',
              duration: '50 min',
              objectives: ['Present arguments to class', 'Evaluate peers\' arguments'],
              introduction: 'Share your arguments and evaluate others!',
              mainActivities: [
                { activity: 'Final Preparation', duration: '10 min', description: 'Prepare for presentations' },
                { activity: 'Presentations', duration: '30 min', description: 'Present arguments to class' },
                { activity: 'Evaluation', duration: '10 min', description: 'Class evaluates arguments' }
              ],
              materials: ['Student arguments', 'Evaluation forms'],
              assessment: 'Presentation quality and evaluation',
              homework: 'Reflect on argument skills'
            }
          ]
        },
        {
          week: 2,
          topic: 'Formal Speaking',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Deliver formal speeches',
            'Use appropriate tone and register',
            'Engage audience effectively'
          ],
          activities: ['Speech preparation', 'Delivery practice', 'Audience engagement'],
          resources: ['Speech templates', 'Rubrics', 'Recording equipment']
        },
        {
          week: 3,
          topic: 'Essay Writing: Introduction',
          strand: 'Writing',
          learningOutcomes: [
            'Understand essay structure',
            'Write effective introductions',
            'Create strong thesis statements'
          ],
          activities: ['Structure analysis', 'Introduction practice', 'Thesis development'],
          resources: ['Essay examples', 'Structure guides', 'Thesis templates']
        },
        {
          week: 4,
          topic: 'Synthesizing Information',
          strand: 'Reading',
          learningOutcomes: [
            'Combine information from multiple sources',
            'Identify common themes across texts',
            'Resolve contradictions between sources'
          ],
          activities: ['Multi-source reading', 'Theme identification', 'Contradiction resolution'],
          resources: ['Multiple source texts', 'Synthesis organizers', 'Comparison charts']
        },
        {
          week: 5,
          topic: 'Academic Discussions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Participate in academic discussions',
            'Use academic language',
            'Support points with evidence'
          ],
          activities: ['Socratic seminars', 'Academic language practice', 'Evidence citing'],
          resources: ['Discussion protocols', 'Academic vocabulary', 'Evidence frames']
        },
        {
          week: 6,
          topic: 'Word Analysis',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze word roots and origins',
            'Use etymology for meaning',
            'Expand academic vocabulary'
          ],
          activities: ['Root study', 'Etymology exploration', 'Vocabulary expansion'],
          resources: ['Root charts', 'Etymology resources', 'Vocabulary journals']
        },
        {
          week: 7,
          topic: 'Essay Writing: Body Paragraphs',
          strand: 'Writing',
          learningOutcomes: [
            'Write developed body paragraphs',
            'Use topic sentences and transitions',
            'Include evidence and analysis'
          ],
          activities: ['Paragraph development', 'Transition practice', 'Evidence integration'],
          resources: ['Paragraph templates', 'Transition lists', 'Evidence guides']
        },
        {
          week: 8,
          topic: 'Media Literacy',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze media messages critically',
            'Identify bias in media',
            'Evaluate media sources'
          ],
          activities: ['Media analysis', 'Bias identification', 'Source evaluation'],
          resources: ['Media samples', 'Analysis frameworks', 'Evaluation criteria']
        },
        {
          week: 9,
          topic: 'Reading Strategies Review',
          strand: 'Reading',
          learningOutcomes: [
            'Apply all comprehension strategies',
            'Self-select strategies for different texts',
            'Monitor comprehension independently'
          ],
          activities: ['Strategy application', 'Strategy selection', 'Self-monitoring'],
          resources: ['Strategy cards', 'Various texts', 'Monitoring checklists']
        },
        {
          week: 10,
          topic: 'Term 1 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate analytical skills',
            'Show essay writing ability',
            'Present formally'
          ],
          activities: ['Analysis assessment', 'Essay writing', 'Formal presentations'],
          resources: ['Assessment materials', 'Rubrics', 'Presentation equipment']
        }
      ]
    },
    {
      term: 'Term 2',
      weeks: 10,
      focus: 'Research & Argument Writing',
      scheme: [
        {
          week: 1,
          topic: 'Research Process',
          strand: 'Reading',
          learningOutcomes: [
            'Plan research projects',
            'Locate reliable sources',
            'Organize research findings'
          ],
          activities: ['Research planning', 'Source location', 'Organization'],
          resources: ['Research planners', 'Source lists', 'Organization tools']
        },
        {
          week: 2,
          topic: 'Presenting Research',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Present research findings',
            'Use visual aids effectively',
            'Answer questions about research'
          ],
          activities: ['Presentation creation', 'Visual aid development', 'Q&A practice'],
          resources: ['Presentation templates', 'Visual aid materials', 'Q&A guidelines']
        },
        {
          week: 3,
          topic: 'Argumentative Writing',
          strand: 'Writing',
          learningOutcomes: [
            'Write argumentative essays',
            'Address counterarguments',
            'Use persuasive techniques'
          ],
          activities: ['Essay planning', 'Counterargument practice', 'Technique application'],
          resources: ['Essay templates', 'Counterargument guides', 'Technique lists']
        },
        {
          week: 4,
          topic: 'Evaluating Sources',
          strand: 'Reading',
          learningOutcomes: [
            'Evaluate source credibility',
            'Identify primary and secondary sources',
            'Cross-reference information'
          ],
          activities: ['Credibility checks', 'Source classification', 'Cross-referencing'],
          resources: ['Evaluation checklists', 'Source examples', 'Verification tools']
        },
        {
          week: 5,
          topic: 'Persuasive Speaking',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Use rhetorical strategies',
            'Appeal to logic, emotion, credibility',
            'Deliver persuasive speeches'
          ],
          activities: ['Rhetorical analysis', 'Appeal practice', 'Speech delivery'],
          resources: ['Rhetoric guides', 'Appeal examples', 'Speech rubrics']
        },
        {
          week: 6,
          topic: 'Note-Taking Strategies',
          strand: 'Reading',
          learningOutcomes: [
            'Use various note-taking methods',
            'Organize notes effectively',
            'Use notes for writing'
          ],
          activities: ['Method practice', 'Note organization', 'Writing from notes'],
          resources: ['Note templates', 'Organization systems', 'Writing guides']
        },
        {
          week: 7,
          topic: 'Essay Revision',
          strand: 'Writing',
          learningOutcomes: [
            'Revise essays effectively',
            'Edit for conventions',
            'Respond to feedback'
          ],
          activities: ['Revision practice', 'Editing workshops', 'Feedback implementation'],
          resources: ['Revision checklists', 'Editing guides', 'Feedback forms']
        },
        {
          week: 8,
          topic: 'Digital Media Analysis',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze digital content critically',
            'Understand digital persuasion techniques',
            'Create digital content responsibly'
          ],
          activities: ['Digital analysis', 'Technique study', 'Content creation'],
          resources: ['Digital media samples', 'Technique guides', 'Creation tools']
        },
        {
          week: 9,
          topic: 'Academic Reading',
          strand: 'Reading',
          learningOutcomes: [
            'Read academic texts',
            'Understand academic structure',
            'Extract key information'
          ],
          activities: ['Academic reading', 'Structure analysis', 'Information extraction'],
          resources: ['Academic texts', 'Structure guides', 'Note templates']
        },
        {
          week: 10,
          topic: 'Term 2 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Complete research project',
            'Write argumentative essay',
            'Present persuasively'
          ],
          activities: ['Project submission', 'Essay assessment', 'Presentations'],
          resources: ['Assessment materials', 'Rubrics', 'Presentation space']
        }
      ]
    },
    {
      term: 'Term 3',
      weeks: 10,
      focus: 'Literary Analysis & Creative Writing',
      scheme: [
        {
          week: 1,
          topic: 'Literary Analysis Introduction',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze literary works deeply',
            'Identify authorial choices',
            'Interpret meaning'
          ],
          activities: ['Deep analysis', 'Choice identification', 'Interpretation'],
          resources: ['Literary texts', 'Analysis frameworks', 'Interpretation guides']
        },
        {
          week: 2,
          topic: 'Literature Discussions',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Lead and participate in literature discussions',
            'Develop interpretations collaboratively',
            'Challenge and build on ideas'
          ],
          activities: ['Discussion leadership', 'Collaborative interpretation', 'Idea development'],
          resources: ['Discussion protocols', 'Interpretation guides', 'Discussion cards']
        },
        {
          week: 3,
          topic: 'Narrative Voice and Style',
          strand: 'Writing',
          learningOutcomes: [
            'Develop personal writing voice',
            'Experiment with narrative styles',
            'Create distinctive prose'
          ],
          activities: ['Voice development', 'Style experimentation', 'Writing workshops'],
          resources: ['Style examples', 'Voice guides', 'Workshop materials']
        },
        {
          week: 4,
          topic: 'Figurative Language',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze use of figurative language',
            'Understand effect of literary devices',
            'Apply analysis to interpretation'
          ],
          activities: ['Device analysis', 'Effect study', 'Interpretation practice'],
          resources: ['Device guides', 'Effect charts', 'Analysis templates']
        },
        {
          week: 5,
          topic: 'Dramatic Performance',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Perform dramatic texts',
            'Interpret characters through performance',
            'Collaborate on productions'
          ],
          activities: ['Text performance', 'Character interpretation', 'Group production'],
          resources: ['Scripts', 'Performance space', 'Props']
        },
        {
          week: 6,
          topic: 'Poetry Analysis',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze poetry structures',
            'Interpret poetic meaning',
            'Appreciate diverse poetry'
          ],
          activities: ['Structure analysis', 'Interpretation practice', 'Poetry appreciation'],
          resources: ['Poetry collections', 'Analysis guides', 'Appreciation journals']
        },
        {
          week: 7,
          topic: 'Creative Writing: Advanced',
          strand: 'Writing',
          learningOutcomes: [
            'Write sophisticated creative pieces',
            'Use advanced techniques',
            'Develop original ideas'
          ],
          activities: ['Advanced writing', 'Technique practice', 'Idea development'],
          resources: ['Writing guides', 'Technique examples', 'Publishing materials']
        },
        {
          week: 8,
          topic: 'Film as Literature',
          strand: 'Viewing',
          learningOutcomes: [
            'Analyze films as literature',
            'Compare literary and film techniques',
            'Evaluate adaptations critically'
          ],
          activities: ['Film analysis', 'Technique comparison', 'Adaptation evaluation'],
          resources: ['Films', 'Comparison guides', 'Evaluation criteria']
        },
        {
          week: 9,
          topic: 'Independent Literature Study',
          strand: 'Reading',
          learningOutcomes: [
            'Conduct independent literary study',
            'Apply all analysis skills',
            'Present literary analysis'
          ],
          activities: ['Independent study', 'Analysis application', 'Presentation preparation'],
          resources: ['Book selection', 'Analysis tools', 'Presentation materials']
        },
        {
          week: 10,
          topic: 'Term 3 Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Demonstrate literary analysis',
            'Show creative writing ability',
            'Present analysis effectively'
          ],
          activities: ['Analysis assessment', 'Creative writing submission', 'Presentations'],
          resources: ['Assessment materials', 'Submission guidelines', 'Presentation space']
        }
      ]
    },
    {
      term: 'Term 4',
      weeks: 10,
      focus: 'Synthesis & Transition to Secondary',
      scheme: [
        {
          week: 1,
          topic: 'Complex Text Analysis',
          strand: 'Reading',
          learningOutcomes: [
            'Analyze complex texts',
            'Synthesize multiple perspectives',
            'Form supported conclusions'
          ],
          activities: ['Complex analysis', 'Perspective synthesis', 'Conclusion formation'],
          resources: ['Complex texts', 'Synthesis tools', 'Conclusion frames']
        },
        {
          week: 2,
          topic: 'Formal Presentations',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Deliver polished formal presentations',
            'Handle Q&A professionally',
            'Use technology effectively'
          ],
          activities: ['Presentation refinement', 'Q&A practice', 'Technology use'],
          resources: ['Presentation tools', 'Q&A guidelines', 'Technology equipment']
        },
        {
          week: 3,
          topic: 'Extended Essay Writing',
          strand: 'Writing',
          learningOutcomes: [
            'Write extended essays',
            'Develop arguments thoroughly',
            'Edit and revise effectively'
          ],
          activities: ['Extended writing', 'Argument development', 'Editing workshops'],
          resources: ['Essay guidelines', 'Development tools', 'Editing checklists']
        },
        {
          week: 4,
          topic: 'Critical Reading Review',
          strand: 'Reading',
          learningOutcomes: [
            'Apply all critical reading skills',
            'Read at Year 5 level independently',
            'Self-assess reading abilities'
          ],
          activities: ['Skill application', 'Independent reading', 'Self-assessment'],
          resources: ['Various texts', 'Assessment tools', 'Self-evaluation forms']
        },
        {
          week: 5,
          topic: 'Collaborative Projects',
          strand: 'Listening & Speaking',
          learningOutcomes: [
            'Collaborate on complex projects',
            'Divide responsibilities effectively',
            'Present collaborative work'
          ],
          activities: ['Project collaboration', 'Task division', 'Group presentations'],
          resources: ['Project guidelines', 'Collaboration tools', 'Presentation materials']
        },
        {
          week: 6,
          topic: 'Writing Portfolio',
          strand: 'Writing',
          learningOutcomes: [
            'Compile writing portfolio',
            'Reflect on writing growth',
            'Select best work'
          ],
          activities: ['Portfolio compilation', 'Reflection writing', 'Selection process'],
          resources: ['Portfolio materials', 'Reflection guides', 'Selection criteria']
        },
        {
          week: 7,
          topic: 'Media Production',
          strand: 'Viewing',
          learningOutcomes: [
            'Produce media content',
            'Apply visual literacy skills',
            'Create effective messages'
          ],
          activities: ['Media production', 'Literacy application', 'Message creation'],
          resources: ['Production tools', 'Literacy guides', 'Creation materials']
        },
        {
          week: 8,
          topic: 'Secondary Preparation',
          strand: 'All Strands',
          learningOutcomes: [
            'Understand secondary expectations',
            'Review primary learning',
            'Set academic goals'
          ],
          activities: ['Expectation discussion', 'Review activities', 'Goal setting'],
          resources: ['Secondary information', 'Review materials', 'Goal worksheets']
        },
        {
          week: 9,
          topic: 'Year 5 Review',
          strand: 'All Strands',
          learningOutcomes: [
            'Review all Year 5 learning',
            'Demonstrate skill mastery',
            'Celebrate growth'
          ],
          activities: ['Comprehensive review', 'Skill demonstration', 'Celebration'],
          resources: ['Review materials', 'Demonstration tasks', 'Celebration supplies']
        },
        {
          week: 10,
          topic: 'Year 5 Final Assessment',
          strand: 'All Strands',
          learningOutcomes: [
            'Complete final assessments',
            'Celebrate achievements',
            'Transition to Year 6'
          ],
          activities: ['Final assessments', 'Awards ceremony', 'Graduation activities'],
          resources: ['Assessment materials', 'Certificates', 'Celebration supplies']
        }
      ]
    }
  ]
};

export default {
  englishYear2,
  englishYear3,
  englishYear4,
  englishYear5
};
